import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { playAlertChime } from '@/lib/alert-chime';
import { queryKeys } from '@/lib/queries/keys';
import { connectSocket, disconnectSocket, getSocket } from '@/lib/socket';
import { useAuthStore } from '@/stores/auth-store';
import { useLiveStore } from '@/stores/live-store';
import { useUiStore } from '@/stores/ui-store';
import type { Alert, PositionEvent } from '@/types/api';

/**
 * The single socket consumer. Mounted once by the app shell: it owns the
 * connection lifecycle, funnels `position` into the live store and `alert` into
 * both the store and the query cache, and narrates connection loss.
 */
export function useRealtimeBridge(onAlert?: (alert: Alert) => void) {
  const client = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.user !== null);
  const applyPosition = useLiveStore((s) => s.applyPosition);
  const setConnection = useLiveStore((s) => s.setConnection);
  const ingestAlert = useLiveStore((s) => s.ingestAlert);
  const reset = useLiveStore((s) => s.reset);

  // Kept in a ref so re-rendering the shell never re-subscribes the socket.
  const onAlertRef = useRef(onAlert);
  useEffect(() => {
    onAlertRef.current = onAlert;
  }, [onAlert]);

  useEffect(() => {
    if (!isAuthenticated) {
      disconnectSocket();
      reset();
      return;
    }

    const socket = connectSocket();
    setConnection(socket.connected ? 'connected' : 'connecting');

    /**
     * Alerts arrive in bursts — a dozen vehicles can brake hard in the same
     * second. Refetching the (200-item) alert queries per event turns that into
     * a request storm that the API rate-limits, and the 429s leave the map
     * showing no alert state at all. Coalesce into one refetch per window.
     */
    const ALERT_REFETCH_MS = 5_000;
    let alertRefetchTimer: number | undefined;

    const refetchAlertsSoon = () => {
      if (alertRefetchTimer !== undefined) return;
      alertRefetchTimer = window.setTimeout(() => {
        alertRefetchTimer = undefined;
        void client.invalidateQueries({ queryKey: queryKeys.alerts.all });
      }, ALERT_REFETCH_MS);
    };

    /**
     * One fixed toast id for the whole session. Socket.IO fires `disconnect`
     * and `connect_error` repeatedly while retrying, and a fresh id per event
     * stacks a new never-expiring toast each time — which is what made
     * "reconnecting" pile up on screen.
     */
    const DOWN_TOAST = 'vt-feed-down';
    let downToastShown = false;
    let downTimer: number | undefined;

    const clearDownToast = () => {
      window.clearTimeout(downTimer);
      downTimer = undefined;
      if (!downToastShown) return false;
      downToastShown = false;
      return true;
    };

    /** A blip should not flash a toast; only a sustained outage is worth one. */
    const scheduleDownToast = () => {
      if (downToastShown || downTimer !== undefined) return;
      downTimer = window.setTimeout(() => {
        downTimer = undefined;
        downToastShown = true;
        toast.loading('Live feed lost — reconnecting…', {
          id: DOWN_TOAST,
          duration: Infinity,
        });
      }, 4000);
    };

    const handleConnect = () => {
      setConnection('connected');
      if (clearDownToast()) {
        toast.success('Live feed restored', { id: DOWN_TOAST, duration: 3000 });
      }
      // The socket was silent while down; resync the snapshot it missed.
      void client.invalidateQueries({ queryKey: queryKeys.vehicles.live() });
      refetchAlertsSoon();
    };

    const handleDisconnect = (reason: string) => {
      // A deliberate client-side disconnect (sign-out) is not an outage.
      if (reason === 'io client disconnect') {
        setConnection('offline');
        clearDownToast();
        toast.dismiss(DOWN_TOAST);
        return;
      }
      setConnection('reconnecting');
      scheduleDownToast();
    };

    const handlePosition = (event: PositionEvent) => applyPosition(event);

    const handleAlert = (alert: Alert) => {
      // The store already flags the vehicle, so the map turns red immediately;
      // the refetch that reconciles counts can wait for the window.
      ingestAlert(alert);
      refetchAlertsSoon();
      onAlertRef.current?.(alert);

      if (useUiStore.getState().alertSound) playAlertChime(alert.severity);
      const notify = alert.severity === 'CRITICAL' ? toast.error : toast.warning;
      notify(alert.title, { description: alert.message });
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.io.on('reconnect_attempt', () => setConnection('reconnecting'));
    socket.on('position', handlePosition);
    socket.on('alert', handleAlert);
    socket.on('connect_error', () => {
      setConnection('reconnecting');
      scheduleDownToast();
    });

    return () => {
      window.clearTimeout(alertRefetchTimer);
      clearDownToast();
      toast.dismiss(DOWN_TOAST);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('position', handlePosition);
      socket.off('alert', handleAlert);
      socket.io.off('reconnect_attempt');
      socket.off('connect_error');
    };
  }, [isAuthenticated, applyPosition, setConnection, ingestAlert, reset, client]);
}

/** Narrows the stream to one asset while a detail view is open. */
export function useVehicleSubscription(vehicleId: string | null | undefined) {
  useEffect(() => {
    if (!vehicleId) return;
    const socket = getSocket();
    const subscribe = () => socket.emit('subscribe:vehicle', { vehicleId });

    if (socket.connected) subscribe();
    socket.on('connect', subscribe);

    return () => {
      socket.off('connect', subscribe);
      if (socket.connected) socket.emit('unsubscribe:vehicle', { vehicleId });
    };
  }, [vehicleId]);
}
